<template>
  <div class="c-section-management-toolbar flex flex-wrap justify-between items-start gap-4 pa-4 rounded-2 bg-base">
    <div class="flex flex-wrap items-center gap-2 flex-1">
      <CSearchFilters
        :customFilterComponents="customFilterComponents"
        :disabled="!!isLoadingItems"
        entity="canvas"
        :filters="metadataFilterValues"
        :include-draft-values="pitcherInfo?.isAnyTypeOfAdmin"
        :initial-search-value="search"
        is-section
        :placeholder="t('canvasUI.components.sections.CSectionManagementToolbar.searchInNameOrTags')"
        v-bind="popularityProps"
        @update:filters="emit('update:metadata-filter-values', $event)"
        @update:search="emit('search', $event)"
      />
    </div>
    <div class="flex gap-2">
      <CButton
        v-if="showSmartFolderToggle"
        v-tooltip="t('canvasUI.components.sections.CSectionManagementToolbar.smartFolders')"
        circle
        :disabled="!!isLoadingItems"
        icon
        @click="toggleSmartFoldersView"
      >
        <CIcon icon="folder-bookmark" size="16px" />
      </CButton>

      <CTableColumnsSettings
        v-if="!isViewHidden"
        :table-columns-settings="tableColumnsSettings"
        :tooltip-text="t('canvasUI.components.sections.CSectionManagementToolbar.configureColumns')"
        @move="(payload) => emit('move', payload)"
        @restore="$emit('restoreColumns')"
        @select="(payload) => emit('select', payload)"
      />
      <CButton v-if="!hideCreate" type="primary" @click="$emit('create:section')">
        {{ t('canvasUI.components.sections.CSectionManagementToolbar.createSection') }}
        <template #icon>
          <CIcon icon="plus" size="16px" />
        </template>
      </CButton>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CTableColumnsSettings, {
  type TableColumnSetting,
} from '@lib/components/CTableColumnsSettings/CTableColumnsSettings.vue'
import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'

import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import useSectionSmartFolderStore from '@lib/stores/sectionSmartFolderStore'

import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { PitcherInfo } from '@lib/types/app'
import type { MetadataTemplateFieldTypeEnum } from '@openapi'

const props = defineProps<{
  hideCreate?: boolean
  search: string
  metadataFilterValues: Record<string, any>
  recommendedSectionIds: string[]
  isLoadingItems?: boolean
  tableColumnsSettings: TableColumnSetting[]
  isViewHidden?: boolean
  customFilterComponents?: {
    type: MetadataTemplateFieldTypeEnum
    name: string
    label: string
    options?: { label: string; value: string }[]
  }[]
}>()

const emit = defineEmits<{
  search: [s: string]
  'create:section': []
  'update:metadata-filter-values': [mf: Record<string, any>]
  move: [payload: { targetItemId: TableColumnSetting['id']; itemId: string }]
  select: [payload: { id: TableColumnSetting['id']; checked: boolean }]
  restoreColumns: []
}>()

const { t } = useI18n()
const { engagingSections, popularSections } = useSectionPopularity()
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)

const { isSmartFoldersView: smartFolderStoreValue } = useSectionSmartFolderStore()

const showSmartFolderToggle = computed(() => launchDarkly.value.enable_smart_folders_for_sections)

const popularityProps = computed(() => ({
  engagingIds: launchDarkly.value.show_popularity_icons ? engagingSections.value.map((item) => item.id) : undefined,
  popularIds: launchDarkly.value.show_popularity_icons ? popularSections.value.map((item) => item.id) : undefined,
  recommendedIds: props.recommendedSectionIds ?? [],
  showLatestFilter: false,
}))

function toggleSmartFoldersView() {
  smartFolderStoreValue.value = !smartFolderStoreValue.value
}
</script>
